/**
 * Timeline strip — the parametric history along the bottom.
 * One chip per feature (icon + name + status styling) and a draggable
 * rollback bar; everything left of the bar is evaluated, everything right
 * is "the future" (faded). Click selects, double-click edits.
 */

import type { App } from '../app';
import type { FeatureId } from '@core/timeline/types';
import { showContextMenu, type ContextItem } from './contextMenu';
import { featureIcon } from './icons';

export function mountTimelinePanel(host: HTMLElement, app: App): void {
  let dragIndex: number | null = null; // rollback position while dragging

  const render = (): void => {
    const t = app.doc.timeline;
    const report = app.doc.lastReport;
    host.innerHTML = '';

    if (t.features.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'nv-tl-empty';
      empty.textContent = 'Timeline — draw a sketch to start building history';
      host.appendChild(empty);
      return;
    }

    const rollbackAt = dragIndex ?? t.rollbackIndex;
    const chips: HTMLElement[] = [];

    const makeBar = (): HTMLElement => {
      const bar = document.createElement('div');
      bar.className = 'nv-rollback';
      bar.title = 'Rollback bar — drag to replay history';
      bar.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
          bar.setPointerCapture(e.pointerId);
        } catch {
          /* synthetic events have no active pointer to capture */
        }
        const onMove = (ev: PointerEvent): void => {
          let idx = 0;
          for (const chip of chips) {
            const r = chip.getBoundingClientRect();
            if (ev.clientX > r.left + r.width / 2) idx = chips.indexOf(chip) + 1;
          }
          if (idx !== dragIndex) {
            dragIndex = idx;
            render();
          }
        };
        const onUp = (): void => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          const target = dragIndex;
          dragIndex = null;
          if (target !== null && target !== t.rollbackIndex) void app.setRollback(target);
          else render();
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      });
      return bar;
    };

    t.features.forEach((f, i) => {
      if (i === rollbackAt) host.appendChild(makeBar());
      const chip = document.createElement('div');
      const status = report?.statuses.get(f.id);
      const cls = ['nv-chip'];
      if (app.selection.includes(f.id)) cls.push('selected');
      if (status?.state === 'error') cls.push('err');
      if (status?.state === 'blocked') cls.push('blocked');
      if (f.suppressed) cls.push('suppressed');
      if (i >= rollbackAt) cls.push('future');
      chip.className = cls.join(' ');
      chip.title =
        status?.state === 'error'
          ? `${f.name}: ${status.message}`
          : status?.state === 'blocked'
            ? `${f.name}: blocked by an upstream error`
            : f.name;
      chip.innerHTML = `${featureIcon(f.type)}<span class="nm">${f.name}</span>`;
      chip.addEventListener('click', (e) => {
        // Shift-click: build a multi-selection (loft stations, grouping).
        if (e.shiftKey) app.toggleSelect(f.id as FeatureId);
        else app.select(f.id as FeatureId);
      });
      chip.addEventListener('dblclick', () => {
        if (f.type === 'sketch') app.editSketch(f.id);
      });
      chip.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        const items: ContextItem[] = [{ label: f.name, header: true }];
        if (f.type === 'sketch') {
          items.push({ label: 'Edit sketch', action: () => app.editSketch(f.id) });
        }
        items.push({
          label: 'Rename…',
          action: () => {
            const name = prompt('Rename feature', f.name);
            if (name?.trim()) {
              app.select(f.id);
              void app.renameSelected(name);
            }
          },
        });
        items.push({
          label: f.suppressed ? 'Unsuppress' : 'Suppress',
          action: () => void app.setSuppressed(f.id, !f.suppressed),
        });
        items.push({
          label: 'Roll back to here',
          separator: true,
          action: () => void app.setRollback(i),
        });
        items.push({
          label: 'Roll to end',
          disabled: t.rollbackIndex === t.features.length,
          action: () => void app.setRollback(t.features.length),
        });
        items.push({
          label: 'Delete',
          separator: true,
          action: () => void app.deleteFeature(f.id),
        });
        showContextMenu(e.clientX, e.clientY, items);
      });
      chips.push(chip);
      host.appendChild(chip);
    });
    if (rollbackAt >= t.features.length) host.appendChild(makeBar());
  };

  app.onUI(render);
  render();
}
